import VisitServiceReviewDialog from './VisitServiceReviewDialog';
// TeachersWorkTimelineReport.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from './api';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';

// Payroll, recorded evidence and teacher entries are deliberately separate.
const evidenceNumber = (value, suffix = '') => value == null ? '—' : `${Number(value).toFixed(2)}${suffix}`;
const routeProviderLabel = (provider) => ({ gps_trip: 'Recorded GPS Trip', policy_excluded: 'Excluded by payroll policy', google_routes: 'Google Routes estimate', ors: 'ORS estimate', time_log_estimate: 'Saved route estimate (provider not recorded)', none: 'No paid route', unavailable: 'Route unavailable' }[provider] || 'Route estimate');
const serviceBasisLabel = (source) => ({ checkin_checkout: 'Check-in/out', teacher_entry_fallback: 'Teacher service log fallback', configured_default: 'Configured/planned fallback', no_show_policy: 'No-show policy', missing_interval: 'Missing interval — review required' }[source] || 'Not recorded');
const tripModeLabel = (trip) => {
  if (!trip) return 'No qualifying GPS Trip';
  const mode = trip.automation?.mode;
  return mode === 'autopilot_full' ? 'GPS Trip · Full AutoPilot' : mode === 'autopilot_confirm' ? 'GPS Trip · Assisted AutoPilot' : 'GPS Trip · mode not recorded';
};
const systemTravelLabel = (item) => item.obj?.travel_provider === 'gps_trip'
  ? tripModeLabel(item.recorded?.travel)
  : item.obj?.travel_provider === 'policy_excluded' ? item.obj?.first_commute_unpaid ? 'Policy: first commute unpaid' : 'Policy: no travel'
  : routeProviderLabel(item.obj?.travel_provider);
function GpsRouteCheck({ item, compact = false }) {
  const check = item.obj?.gps_route_check;
  if (!check || ['no_gps', 'policy_excluded'].includes(check.status)) return null;
  const provider = routeProviderLabel(check.estimate_provider);
  const excess = (value) => value == null ? 'zero estimate baseline' : `${Number(value).toFixed(2)}%`;
  const message = check.status === 'gps_exceeds_tolerance'
    ? `${provider} used: GPS ${check.exceeded.join(' and ')} exceeds the ${check.threshold_percent}% tolerance.`
    : check.status === 'gps_unchecked_estimate_unavailable' ? 'GPS used — route comparison unavailable.'
    : check.status === 'long_gap_estimate_only' ? 'Long scheduled gap: route estimate required; GPS tolerance check skipped.'
    : `GPS used — within ${check.threshold_percent}% route tolerance.`;
  return <div className={`mt-1 text-[10px] ${check.status === 'gps_exceeds_tolerance' || check.status === 'gps_unchecked_estimate_unavailable' ? 'text-amber-800' : 'text-slate-600'}`}>
    <div>{message}</div>
    {!compact && <><div>GPS: {evidenceNumber(check.gps_miles, ' mi')} / {evidenceNumber(check.gps_minutes, ' min')} · Estimate: {evidenceNumber(check.estimate_miles, ' mi')} / {evidenceNumber(check.estimate_minutes, ' min')} · {provider}</div>
    {check.status === 'gps_exceeds_tolerance' || check.status === 'gps_within_tolerance' ? <div>Distance deviation: {excess(check.distance_excess_percent)} · Time deviation: {excess(check.time_excess_percent)}</div> : null}
    <div>This is a payroll tolerance rule, not a finding about personal travel. Google estimates do not include traffic.</div></>}
  </div>;
}
const travelSourceSummary = (items) => {
  const sources = new Map();
  for (const item of items) { const label = systemTravelLabel(item); sources.set(label, (sources.get(label) || 0) + 1); }
  return Array.from(sources, ([label, count]) => `${label}: ${count}`).join(' · ');
};
const teacherTravelLabel = (item) => item.teacher_trip?.uses_system_fallback
  ? `${item.teacher_trip.miles_source === 'teacher_log' || item.teacher_trip.minutes_source === 'teacher_log' ? 'Teacher log + ' : ''}System fallback: ${systemTravelLabel(item)}`
  : item.teacher_estimate?.travel_cost != null ? 'Teacher manual log' : 'Travel data unavailable';
function TeacherTripQuantity({ item, field, suffix }) {
  const trip = item.teacher_trip;
  const key = field === 'miles' ? 'miles_source' : 'minutes_source';
  const value = trip ? trip[field] : item.teacher_entry?.[field];
  return <div className="text-[10px] text-slate-600">Teacher: {evidenceNumber(value, suffix)}<div>{trip?.[key] === 'system_fallback' ? `System fallback: ${systemTravelLabel(item)}` : trip?.[key] === 'teacher_log' ? 'Manual log' : 'Not recorded'}</div></div>;
}

const comparisonMoney = (value, signed = false) => value == null ? '—' : `${signed && value > 0 ? '+' : value < 0 ? '−' : ''}$${Math.abs(Number(value)).toFixed(2)}`;
function MoneyComparison({ system, estimate, difference, compact = false }) {
  return <div className={compact ? 'text-[11px]' : ''}>
    <div className="font-semibold">System: {comparisonMoney(system)}</div>
    <div className="text-slate-600">Teacher: {comparisonMoney(estimate)}</div>
    <div className="text-[10px] text-indigo-700">Δ: {comparisonMoney(difference, true)}</div>
  </div>;
}

// Footer sums keep available manual amounts visible, but never label partial data complete.
function summarizeTeacherEntries(list) {
  const sumKnown = (values) => {
    const known = values.filter((v) => v != null && Number.isFinite(Number(v)));
    return known.length ? known.reduce((sum, v) => sum + Number(v), 0) : null;
  };
  const participating = list.filter((it) => Object.entries(it.teacher_entry || {}).some(([key, value]) =>
    ['service_minutes', 'drive_minutes', 'miles', 'extra', 'break_minutes'].includes(key) && value != null) || it.teacher_trip?.uses_system_fallback);
  const service = sumKnown(list.map((it) => it.teacher_estimate?.service_cost));
  const travel = sumKnown(list.map((it) => it.teacher_estimate?.travel_cost));
  const extra = sumKnown(list.map((it) => it.teacher_entry?.extra));
  const bonus = participating.length ? participating.reduce((sum, it) => sum + Number(it.teacher_estimate?.bonus ?? it.bonus ?? 0), 0) : null;
  const mentor = participating.length ? participating.reduce((sum, it) => sum + Number(it.mentor_fee || 0), 0) : null;
  const total = participating.length && (service != null || travel != null || extra != null)
    ? Number(service || 0) + Number(travel || 0) + Number(extra || 0) + Number(bonus || 0) + Number(mentor || 0) : null;
  const completeCount = list.filter((it) => it.teacher_estimate?.complete).length;
  return {
    service, travel, extra, bonus, mentor, total,
    rawHours: sumKnown(list.map((it) => it.teacher_entry?.service_minutes == null ? null : it.teacher_entry.service_minutes / 60)),
    rawVisitHours: sumKnown(list.filter((it) => !isWorkItem(it)).map((it) => it.teacher_entry?.service_minutes == null ? null : it.teacher_entry.service_minutes / 60)),
    rawWorkHours: sumKnown(list.filter(isWorkItem).map((it) => it.teacher_entry?.service_minutes == null ? null : it.teacher_entry.service_minutes / 60)),
    hours: sumKnown(list.map((it) => it.teacher_estimate?.service_minutes == null ? null : it.teacher_estimate.service_minutes / 60)),
    visitHours: sumKnown(list.filter((it) => !isWorkItem(it)).map((it) => it.teacher_estimate?.service_minutes == null ? null : it.teacher_estimate.service_minutes / 60)),
    workHours: sumKnown(list.filter(isWorkItem).map((it) => it.teacher_estimate?.service_minutes == null ? null : it.teacher_estimate.service_minutes / 60)),
    miles: sumKnown(list.map((it) => it.teacher_trip ? it.teacher_trip.miles : it.teacher_entry?.miles)),
    driveMinutes: sumKnown(list.map((it) => it.teacher_trip ? it.teacher_trip.drive_minutes : it.teacher_entry?.drive_minutes)),
    breakMinutes: sumKnown(list.map((it) => it.teacher_entry?.break_minutes)),
    count: participating.length, completeCount, fallbackCount: list.filter((it) => it.teacher_trip?.uses_system_fallback).length, reviewCount: list.filter((it) => it.service_assessment?.teacher?.requires_review).length, itemCount: list.length,
    complete: list.length > 0 && completeCount === list.length,
  };
}
function TeacherHours({ entered, used }) {
  return <div>
    <div>{evidenceNumber(entered, ' h entered')}</div>
    <div className="text-[10px] font-medium text-indigo-700">(for calculation: {evidenceNumber(used, ' h')})</div>
  </div>;
}
function TeacherServiceCalculation({ item }) {
  const raw = item.teacher_entry?.service_minutes;
  const used = item.teacher_estimate?.service_minutes;
  const assessment = item.service_assessment?.teacher;
  const adjusted = raw != null && used != null && Math.abs(raw - used) > 1e-9;
  return <div className="mt-1 text-[10px] text-indigo-800">
    {adjusted && <div className="font-semibold">{evidenceNumber(raw)} → {evidenceNumber(used)} min for calculation</div>}
    {used != null && <div>{evidenceNumber(used)} min ÷ 60 × {comparisonMoney(item.obj?.service_rate)}/h = {comparisonMoney(item.teacher_estimate?.service_cost)}</div>}
    {assessment?.status === 'above_maximum_tolerance' && <div>Capped at {assessment.rule_type} max {evidenceNumber(assessment.maximum)} min ({assessment?.excess_percent === 0 ? 'hard maximum; no upper tolerance' : `allowed up to ${evidenceNumber(assessment?.upper_limit_minutes)} min`}).</div>}
    {assessment?.status === 'below_minimum_tolerance' && <div>Below {evidenceNumber(assessment.lower_limit_minutes)} min: visit service and visit bonus excluded.</div>}
  </div>;
}

function TeacherCoverage({ summary }) {
  return <div className="text-[10px] font-normal text-slate-600">{summary.count === 0 ? 'No manual entries' : summary.complete ? 'Inputs complete' : 'Partial — available amounts only'} · complete {summary.completeCount}/{summary.itemCount} · system travel fallback: {summary.fallbackCount || 0} · service review: {summary.reviewCount || 0}</div>;
}
function ApprovedPayrollRow({ value, daily = false }) {
  if (!value) return null;
  return <tr className="bg-emerald-50 font-semibold">
    <td className="border p-2" colSpan={daily ? 2 : 1}>Effective payroll — admin approvals + system rules<div className="text-[10px] font-normal text-emerald-800">{value.approved_count || 0} active service approvals · alternative total, not additional pay</div></td>
    {daily ? <td className="border p-2 text-right">{toFixed(Number(value.visit_hours || 0) + Number(value.work_hours || 0))}</td> : <><td className="border p-2 text-right">{toFixed(value.visit_hours)}</td><td className="border p-2 text-right">{toFixed(value.work_hours)}</td></>}
    {daily ? <><td className="border p-2 text-right">{toFixed(value.distance_mi)}</td><td className="border p-2 text-right">{toFixed(value.drive_minutes)}</td></> : <><td className="border p-2 text-right">{toFixed(value.drive_minutes)}</td><td className="border p-2 text-right">{toFixed(value.distance_mi)}</td></>}
    <td className="border p-2 text-right">{comparisonMoney(value.service_cost)}</td><td className="border p-2 text-right">{comparisonMoney(value.travel_cost)}</td><td className="border p-2 text-right">—</td>
    <td className="border p-2 text-right">{comparisonMoney(value.bonus)} / {comparisonMoney(value.mentor_fee)}</td><td className="border p-2 text-right">{comparisonMoney(value.total)}</td>
  </tr>;
}

function PeriodCalculations({ system, hours, teacher, items, approved }) {
  const difference = teacher.complete && teacher.total != null ? teacher.total - Number(system?.total || 0) : null;
  return <div className="my-3 overflow-x-auto border rounded bg-white print:break-inside-avoid">
    <table className="w-full text-xs">
      <thead className="bg-gray-100"><tr>
        {['Calculation', 'Visit hours', 'Work hours', 'Drive min', 'Miles', 'Service ($)', 'Travel ($)', 'Extra ($)', 'Bonus / Mentor ($)', 'Total ($)'].map((label) => <th key={label} className="border p-2 text-right first:text-left">{label}</th>)}
      </tr></thead>
      <tbody>
        <tr className="bg-blue-50 font-semibold"><td className="border p-2">System calculation<div className="text-[10px] font-normal text-slate-600">{travelSourceSummary(items)} · service review: {items.filter((it) => it.service_assessment?.system?.requires_review).length}</div></td>
          <td className="border p-2 text-right">{evidenceNumber(hours?.visit_hours)}</td><td className="border p-2 text-right">{evidenceNumber(hours?.work_hours)}</td>
          <td className="border p-2 text-right">{evidenceNumber(system?.drive_minutes)}</td><td className="border p-2 text-right">{evidenceNumber(system?.distance_mi)}</td>
          <td className="border p-2 text-right">{comparisonMoney(system?.service_cost)}</td><td className="border p-2 text-right">{comparisonMoney(system?.travel_cost)}</td>
          <td className="border p-2 text-right">—</td><td className="border p-2 text-right">{comparisonMoney(system?.bonus)} / {comparisonMoney(system?.mentor_fee)}</td><td className="border p-2 text-right">{comparisonMoney(system?.total)}</td>
        </tr>
        <tr className="bg-amber-50 font-semibold"><td className="border p-2">Teacher calculation<TeacherCoverage summary={teacher} /></td>
          <td className="border p-2 text-right"><TeacherHours entered={teacher.rawVisitHours} used={teacher.visitHours} /></td><td className="border p-2 text-right"><TeacherHours entered={teacher.rawWorkHours} used={teacher.workHours} /></td>
          <td className="border p-2 text-right">{evidenceNumber(teacher.driveMinutes)}</td><td className="border p-2 text-right">{evidenceNumber(teacher.miles)}</td>
          <td className="border p-2 text-right">{comparisonMoney(teacher.service)}</td><td className="border p-2 text-right">{comparisonMoney(teacher.travel)}</td>
          <td className="border p-2 text-right">{comparisonMoney(teacher.extra)}</td><td className="border p-2 text-right">{comparisonMoney(teacher.bonus)} / {comparisonMoney(teacher.mentor)}</td><td className="border p-2 text-right">{comparisonMoney(teacher.total)}</td>
        </tr>
        <ApprovedPayrollRow value={approved} />
        <tr><td colSpan={9} className="border p-2 text-indigo-700">Difference: Teacher − System{!teacher.complete ? ' · unavailable until service/travel inputs are complete' : ''}</td><td className="border p-2 text-right text-indigo-700">{comparisonMoney(difference, true)}</td></tr>
      </tbody>
    </table>
  </div>;
}

function ServiceAssessment({ item, model, compact = false }) {
  const assessment = item.service_assessment?.[model];
  if (!assessment || ['no_show_policy', 'no_show_manual_comparison'].includes(assessment.status)) return null;
  if (compact && assessment.status === 'accepted') return null;
  const label = model === 'system' ? 'System' : 'Teacher';
  const explanation = {
    below_minimum_tolerance: 'Below allowed minimum: visit service and visit bonus excluded; review required.',
    above_maximum_tolerance: 'Above allowed maximum: service capped at the discipline maximum; review required.',
    missing_interval: 'Missing/invalid service interval: no automatic service or visit bonus.',
    invalid_rule: 'Invalid service duration settings: review required.',
    accepted: 'Actual minutes accepted; no minimum uplift.',
  }[assessment.status] || assessment.status;
  return <div className={`mt-1 ${compact ? 'text-[10px]' : 'text-sm'} ${assessment.requires_review ? 'text-amber-800' : 'text-slate-600'}`}>
    <div><strong>{label} service:</strong> {evidenceNumber(assessment.raw_minutes, ' min')} → {evidenceNumber(assessment.used_minutes, ' min')} for calculation.</div>
    <div>{explanation}</div>
    {!compact && <div>Type {assessment.rule_type} · min {evidenceNumber(assessment.minimum)} / max {evidenceNumber(assessment.maximum)} min · accepted {evidenceNumber(assessment.lower_limit_minutes)}–{evidenceNumber(assessment.upper_limit_minutes)} min ({assessment.shortfall_percent}% below minimum / {assessment.excess_percent}% above maximum). Paid breaks are separate from visit service duration.</div>}
  </div>;
}

function CalculationDetails({ item }) {
  const recorded = item.recorded || {};
  const manual = item.teacher_entry || {};
  const travel = recorded.travel;
  return <div className="space-y-3 text-sm text-slate-700">
      <div><strong>Recorded service:</strong> {evidenceNumber(recorded.service_minutes, ' min')} · {recorded.service_source === 'checkin_checkout' ? 'Check-in/out' : 'Not recorded'}</div>
      <div>Check-in: {safe(recorded.checkin_time, '—')} · Check-out: {safe(recorded.checkout_time, '—')}</div>
      <div><strong>Payroll rule:</strong> {safe(item.obj?.rule_label, item.obj?.service_source === 'checkin_checkout' ? 'Recorded work duration' : item.obj?.service_source === 'teacher_entry_fallback' ? 'Teacher service entry fallback' : 'Configured/planned duration fallback')}</div>
      <div>Service basis: {serviceBasisLabel(item.obj?.service_source)} · Paid break: {evidenceNumber(item.paid_break_min, ' min')}</div>
      <div><strong>Recorded travel:</strong> {tripModeLabel(travel)} · {evidenceNumber(travel?.miles, ' mi')} · {evidenceNumber(travel?.minutes, ' min')}</div>
      {travel && <div>Trip {travel.trip_id} · {safe(travel.started_at, '—')} → {safe(travel.stopped_at, '—')}{travel.automation ? ` · AutoPilot session ${travel.automation.session_id}` : ''}</div>}
      <ServiceAssessment item={item} model="system" />
      <ServiceAssessment item={item} model="teacher" />
      <div><strong>Teacher entry:</strong> service {evidenceNumber(manual.service_minutes, ' min')} · drive {evidenceNumber(manual.drive_minutes, ' min')} · {evidenceNumber(manual.miles, ' mi')} · extra {evidenceNumber(manual.extra, ' USD')} · break {evidenceNumber(manual.break_minutes, ' min')}</div>
      <div>Manual service: {safe(manual.service_start, '—')} → {safe(manual.service_end, '—')} · Manual drive: {safe(manual.drive_start, '—')} → {safe(manual.drive_end, '—')}</div>
      <GpsRouteCheck item={item} />
      <div><strong>Teacher travel used in calculation:</strong> {evidenceNumber(item.teacher_trip?.miles, ' mi')} · {evidenceNumber(item.teacher_trip?.drive_minutes, ' min')} · {teacherTravelLabel(item)}</div>
      <div><strong>Teacher monetary estimate:</strong> {safe(manual.source, 'No manual source')} · service {comparisonMoney(item.teacher_estimate?.service_cost)} · travel {comparisonMoney(item.teacher_estimate?.travel_cost)} · total {comparisonMoney(item.teacher_estimate?.total)}.</div>
      <div>Teacher amounts use entered service time and manual travel quantities, with labeled system travel fallbacks, at the listed rates. Item totals include the visit bonus allowed for each model, unchanged mentor fees and declared teacher extra costs. Missing travel fields use explicitly labeled system fallbacks. Original manual entries above remain unchanged; missing service time is not replaced by travel time.</div>
      <div>Daily totals use full calculation precision; displayed rows may differ by a cent when added.</div>
  </div>;
}

function ServiceAssessmentLink({ item, model, onOpen }) {
  const assessment = item.service_assessment?.[model];
  if (!assessment || ['accepted', 'no_show_policy', 'no_show_manual_comparison'].includes(assessment.status)) return null;
  const label = model === 'system' ? 'System' : 'Teacher';
  const summary = `${evidenceNumber(assessment.raw_minutes)} → ${evidenceNumber(assessment.used_minutes, ' min')}`;
  return <div className="mt-1 text-[10px]">
    <button type="button" data-report-details-trigger onClick={() => onOpen({ item, model })}
      aria-haspopup="dialog" className="text-amber-800 underline underline-offset-2 hover:text-amber-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600">
      {label} service: {summary}
    </button>
    <span className="report-service-print-summary text-amber-800">{label}: {summary}</span>
  </div>;
}

function CalculationDetailsDialog({ selection, onClose }) {
  const panel = useRef(null);
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); close.current(); return; }
      if (event.key !== 'Tab') return;
      const controls = [...panel.current.querySelectorAll('button:not(:disabled),a[href],[tabindex="0"]')];
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); document.body.style.overflow = overflow; previous?.focus?.(); };
  }, []);
  const { item, model } = selection;
  const title = model ? `${model === 'system' ? 'System' : 'Teacher'} service calculation` : 'Calculation details';
  return createPortal(<div data-report-details-modal className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-2 sm:p-6" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="report-calculation-title" className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
      <header className="flex items-start justify-between gap-3 border-b bg-slate-50 px-5 py-4">
        <div><h2 id="report-calculation-title" className="text-lg font-semibold text-slate-900">{title}</h2><p className="mt-1 text-sm text-slate-600">{safe(item.label)} · {safe(item.date)} · {safe(item.timeSlot, '')}</p></div>
        <button type="button" onClick={onClose} className="rounded-lg border bg-white px-3 py-2 text-sm">Close</button>
      </header>
      <div className="overflow-y-auto p-5">
        {model ? <ServiceAssessment item={item} model={model} /> : <CalculationDetails item={item} />}
      </div>
      <footer className="flex items-center justify-between gap-3 border-t bg-slate-50 px-5 py-3"><p className="text-xs text-slate-500">Details are for review and are not included in the printed report.</p><button type="button" onClick={onClose} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">Done</button></footer>
    </section>
  </div>, document.body);
}

// ---------- Helpers ----------
const toFixed = (v, n = 2) => {
  const num = Number(v);
  return Number.isFinite(num) ? num.toFixed(n) : (0).toFixed(n);
};
const fmtMoney = (v) => toFixed(v, 2);

const safe = (v, fallback = '-') => (v === null || v === undefined || v === '' ? fallback : v);

const toISODate = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

// ✅ safe fallback without mixing ?? and ||

function groupBy(array, keyFn) {
  const out = {};
  for (const item of array || []) {
    const key = keyFn(item);
    (out[key] ||= []).push(item);
  }
  return out;
}

const minutesToHours = (min) => {
  const m = Number(min || 0);
  if (!Number.isFinite(m) || m <= 0) return '0.00';
  return (m / 60).toFixed(2);
};

// --- show miles-only string for travel distance (BE formula often has km + (mi)) ---
function milesOnlyDistanceFormula(s) {
  const str = String(s || '');
  if (!str) return '';
  const re = /(=\s*)([\d.]+)\s*km\s*\(([\d.]+)\s*mi\)/i;
  if (re.test(str)) return str.replace(re, `$1$3 mi`);
  return str;
}

/** =========================================================
 * ✅ Robust kind/date detection (fix for missing work blocks)
 * ========================================================= */
function isWorkItem(it) {
  if (!it) return false;
  const k = String(it.kind || '').toLowerCase();
  if (k === 'work') return true;
  // tolerate variations
  if (k.includes('work')) return true;
  if (it.work_type != null) return true;
  const lbl = String(it.label || '').toLowerCase();
  if (lbl.startsWith('work:')) return true;
  return false;
}

function getItemDateKey(it) {
  // prefer explicit `date`
  const d1 = String(it?.date || '').slice(0, 10);
  if (d1) return d1;

  // tolerate old/report-like keys
  const d2 = String(it?.visit_date || '').slice(0, 10);
  if (d2) return d2;

  const d3 = String(it?.work_date || '').slice(0, 10);
  if (d3) return d3;

  // last resort
  const sched = String(it?.scheduled_at || '').slice(0, 10);
  if (sched) return sched;

  return 'Unknown date';
}

function getItemKindNormalized(it) {
  return isWorkItem(it) ? 'work' : 'visit';
}

/** =========================================================
 * Default legenda (fallback)
 * ========================================================= */
const defaultLegendDurationRules = {
  EI: { type: 'range', default: 45, min: 45, max: 45 },
  ST: { type: 'range', default: 45, min: 45, max: 45 },
  PI: { type: 'range', default: 90, min: 90, max: 90 },
  ABA: { type: 'range', default: 90, min: 45, max: 240 },
  OT: { type: 'range', default: 45, min: 45, max: 90 },
  PT: { type: 'range', default: 45, min: 45, max: 90 },
  EVENT: { type: 'range', default: 90, min: 45, max: 240 },
  generic: {
    OBJ_MIN_TRIGGER_MIN: 30,
    OBJ_BILLED_MIN: 45,
    OBJ_BILLED_MAX: 60,
    NOSHOW_MAX_MIN: 15,
    FOUR_HOURS_MIN: 240,
    PAID_BREAK_MIN: 10,
    PAYROLL_CHAIN_GAP_ENABLED: true,
    PAYROLL_CHAIN_GAP_MAX_MIN: 90,
  },
};

function normalizeRuleForLegend(rule, fallback) {
  const r = rule && typeof rule === 'object' ? rule : {};
  const fb = fallback && typeof fallback === 'object' ? fallback : {};

  // legacy fixed support
  if (r.type === 'fixed' || r.fixed != null) {
    const v = Number(r.fixed ?? r.default ?? fb.fixed ?? fb.default ?? 0) || 0;
    return { type: 'range', default: v, min: v, max: v };
  }

  const def = Number(r.default ?? fb.default ?? 0) || 0;
  const min = Number(r.min ?? fb.min ?? def) || def;
  const max = Number(r.max ?? fb.max ?? def) || def;

  return {
    type: 'range',
    default: def,
    min: Math.min(min, def, max),
    max: Math.max(max, def, min),
  };
}

// ---------- Discipline badges ----------
function getDisciplineBadgeConfig(serviceType) {
  switch (serviceType) {
    case 'EI':
      return { label: 'EI', bg: 'bg-blue-100', border: 'border-blue-200', text: 'text-blue-800' };
    case 'PI':
      return { label: 'PI', bg: 'bg-orange-100', border: 'border-orange-200', text: 'text-orange-800' };
    case 'ABA':
      return { label: 'ABA', bg: 'bg-purple-100', border: 'border-purple-200', text: 'text-purple-800' };
    case 'OT':
      return { label: 'OT', bg: 'bg-teal-100', border: 'border-teal-200', text: 'text-teal-800' };
    case 'PT':
      return { label: 'PT', bg: 'bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-800' };
    case 'ST':
      return { label: 'ST', bg: 'bg-green-100', border: 'border-green-200', text: 'text-green-800' };
    default:
      return null;
  }
}

function DisciplineBadge({ serviceType }) {
  const cfg = getDisciplineBadgeConfig(serviceType);
  if (!cfg) return null;
  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold',
        'border',
        cfg.bg,
        cfg.border,
        cfg.text,
      ].join(' ')}
    >
      {cfg.label}
    </span>
  );
}

function TelehealthBadge() {
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-pink-50 border border-pink-200 text-pink-700">
      <svg className="inline-block mr-1" width={12} height={12} viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M4 6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v2.382l2.553-1.702A1 1 0 0 1 19.999 7v10a1 1 0 0 1-1.446.894L15 16.191V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
        />
      </svg>
      Telehealth
    </span>
  );
}

function MentorBadge({ stage, amount }) {
  const st = String(stage || '').toLowerCase();
  const labelStage = st === 'shadow' ? 'shadow' : st === 'supervised' ? 'supervised' : st || '';
  const money = Number(amount || 0) > 0 ? ` +$${fmtMoney(amount)}` : '';
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-indigo-50 border border-indigo-200 text-indigo-700">
      <svg className="inline-block mr-1" width={12} height={12} viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M12 3a5 5 0 0 0-5 5v1H6a2 2 0 0 0-2 2v7a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-7a2 2 0 0 0-2-2h-1V8a5 5 0 0 0-5-5m-3 6V8a3 3 0 0 1 6 0v1z"
        />
      </svg>
      Mentor{labelStage ? ` (${labelStage})` : ''}
      {money}
    </span>
  );
}

// ---------- Work badge ----------
function workBadgeCfg(workType) {
  const t = String(workType || '').toLowerCase();
  switch (t) {
    case 'admin':
      return { label: 'Admin', bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700' };
    case 'documentation':
      return { label: 'Docs', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' };
    case 'training':
      return { label: 'Training', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' };
    case 'research':
      return { label: 'Research', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' };
    case 'meeting':
      return { label: 'Meeting', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' };
    default:
      return { label: 'Work', bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
  }
}

function WorkBadge({ workType }) {
  const cfg = workBadgeCfg(workType);
  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold border',
        cfg.bg,
        cfg.border,
        cfg.text,
      ].join(' ')}
    >
      {cfg.label}
    </span>
  );
}

function TravelPolicyBadge({ policy, gapMinutes }) {
  const p = String(policy || '').toLowerCase();
  if (p !== 'paid_estimated_after_gap') return null;

  const gap = Number(gapMinutes);
  const gapLabel = Number.isFinite(gap) ? ` ${gap.toFixed(0)}m` : '';

  return (
    <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-semibold">
      Long gap{gapLabel}
    </span>
  );
}

function EvvBadge({ required, status }) {
  if (Number(required || 0) !== 1) {
    return (
      <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-slate-50 border border-slate-200 text-slate-600">
        EVV not required
      </span>
    );
  }

  const s = String(status || 'pending').toLowerCase();
  if (s === 'complete') {
    return (
      <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-emerald-50 border border-emerald-200 text-emerald-700">
        EVV complete
      </span>
    );
  }
  if (s === 'exception') {
    return (
      <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-rose-50 border border-rose-200 text-rose-700">
        EVV exception
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-amber-50 border border-amber-200 text-amber-800">
      EVV pending
    </span>
  );
}

// ---------- Center Loading Toast ----------
function LoadingToast({ show, text = 'Generating report…' }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
      <div className="absolute inset-0 bg-black/10" />
      <div className="pointer-events-none bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
        <span className="inline-block w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        <div className="text-sm font-semibold text-slate-800">{text}</div>
      </div>
    </div>
  );
}

export default function TeachersWorkTimelineReport() {
  const [teachers, setTeachers] = useState([]);
  const [administrativePayroll, setAdministrativePayroll] = useState(null);
  const [reviewVisitId, setReviewVisitId] = useState(null);
  const [calculationSelection, setCalculationSelection] = useState(null);
  const [candidateId, setCandidateId] = useState('');
  const [filters, setFilters] = useState({
    teacher_id: '',
    date_from: '',
    date_to: '',
  });

  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState([]);
  const [dailyTotals, setDailyTotals] = useState([]);
  const [totals, setTotals] = useState({
    obj: { service_cost: 0, travel_cost: 0, bonus: 0, mentor_fee: 0, total: 0, paid_break_minutes: 0, paid_break_cost: 0, drive_minutes: 0, drive_hours: 0, distance_km: 0, distance_mi: 0 },
    subj: { service_cost: 0, travel_cost: 0, extra: 0, bonus: 0, mentor_fee: 0, total: 0, paid_break_minutes: 0, paid_break_cost: 0, drive_minutes: 0, drive_hours: 0, distance_km: 0, distance_mi: 0 },
  });

  const [hoursBreakdown, setHoursBreakdown] = useState({
    obj: { visit_hours: 0, work_hours: 0, drive_hours: 0 },
    subj: { visit_hours: 0, work_hours: 0, drive_hours: 0 },
  });

  const [rates, setRates] = useState(null);

  const [teacherName, setTeacherName] = useState('');
  const [teacherAddress, setTeacherAddress] = useState('');
  const [serverTimeStr, setServerTimeStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [brandName, setBrandName] = useState('EarlyCare');
  const [brandLogoUrl, setBrandLogoUrl] = useState('/VisitOMS_logo.png');

  const [durationRules, setDurationRules] = useState(null);
  const [mentorFees, setMentorFees] = useState({ EI: 0, PI: 0, ABA: 0, OT: 0, PT: 0, ST: 0 });
  const [meta, setMeta] = useState(null);

  const reportRef = useRef();

  const selectedTeacher = useMemo(() => {
    const tid = Number(filters.teacher_id || 0);
    return (teachers || []).find((t) => Number(t.id) === tid) || null;
  }, [teachers, filters.teacher_id]);

  // --- Init load ---
  useEffect(() => {
    (async () => {
      try {
        const [tRes, timeRes, brandRes, logoRes, durationRes, mentorRes] = await Promise.all([
          API.get('/teachers'),
          API.get('/server-time').catch(() => ({ data: {} })),
          API.get('/brand-settings').catch(() => ({ data: {} })),
          API.get('/brand-logo').catch(() => ({ data: {} })),
          API.get('/service-duration-settings').catch(() => ({ data: null })),
          API.get('/mentor-fee-settings').catch(() => ({ data: { EI: 0, PI: 0, ABA: 0, OT: 0, PT: 0, ST: 0 } })),
        ]);

        setTeachers(Array.isArray(tRes.data) ? tRes.data : []);
        setServerTimeStr(timeRes?.data?.serverTime || '');

        const name = (brandRes.data?.brand_name || '').trim();
        if (name) setBrandName(name);

        const url = logoRes.data?.logo_url || null;
        setBrandLogoUrl(url || '/VisitOMS_logo.png');

        if (durationRes && durationRes.data) setDurationRules(durationRes.data);

        if (mentorRes && mentorRes.data) {
          setMentorFees({
            EI: Number(mentorRes.data.EI || 0),
            PI: Number(mentorRes.data.PI || 0),
            ABA: Number(mentorRes.data.ABA || 0),
            OT: Number(mentorRes.data.OT || 0),
            PT: Number(mentorRes.data.PT || 0),
            ST: Number(mentorRes.data.ST || 0),
          });
        }
      } catch (e) {
        console.warn('Init load failed (TeachersWorkTimelineReport):', e);
      }
    })();
  }, []);

  // --- Default last 30 days ---
  useEffect(() => {
    const now = serverTimeStr ? new Date(serverTimeStr) : new Date();
    if (Number.isNaN(now.getTime())) return;

    setFilters((f) => {
      if (f.date_from && f.date_to) return f;
      const end = toISODate(now);
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 2);
      const start = toISODate(startDate);
      return { ...f, date_from: start, date_to: end };
    });
  }, [serverTimeStr]);

  const fetchReport = async () => {
    if (!filters.teacher_id) {
      alert('Please choose a teacher.');
      return;
    }

    setLoading(true);
    setLoadError('');

    try {
      const payload = {
        teacher_id: filters.teacher_id,
        date_from: filters.date_from,
        date_to: filters.date_to,
      };

      const res = await API.post('/reports/teachers-work-timeline', payload);

      setAdministrativePayroll(res.data?.administrativePayroll || null);
      setCandidateId('');
      const rawItems = Array.isArray(res.data?.items) ? res.data.items : [];

      // ✅ Normalize items so FE can always recognize WORK and DATE
      const normalizedItems = rawItems.map((it) => {
        const kind = getItemKindNormalized(it);
        const date = getItemDateKey(it);
        return { ...it, kind, date };
      });

      setItems(normalizedItems);
      setSegments(Array.isArray(res.data?.segments) ? res.data.segments : []);
      setDailyTotals(Array.isArray(res.data?.dailyTotals) ? res.data.dailyTotals : []);

      setTotals(
        res.data?.totals || {
          obj: { service_cost: 0, travel_cost: 0, bonus: 0, mentor_fee: 0, total: 0, paid_break_minutes: 0, paid_break_cost: 0, drive_minutes: 0, drive_hours: 0, distance_km: 0, distance_mi: 0 },
          subj: { service_cost: 0, travel_cost: 0, extra: 0, bonus: 0, mentor_fee: 0, total: 0, paid_break_minutes: 0, paid_break_cost: 0, drive_minutes: 0, drive_hours: 0, distance_km: 0, distance_mi: 0 },
        }
      );

      setHoursBreakdown(
        res.data?.hoursBreakdown || {
          obj: { visit_hours: 0, work_hours: 0, drive_hours: 0 },
          subj: { visit_hours: 0, work_hours: 0, drive_hours: 0 },
        }
      );

      if (res.data?.rates) setRates(res.data.rates);
      if (res.data?.durationRules) setDurationRules(res.data.durationRules);
      if (res.data?.mentorFees) {
        setMentorFees({
          EI: Number(res.data.mentorFees.EI || 0),
          PI: Number(res.data.mentorFees.PI || 0),
          ABA: Number(res.data.mentorFees.ABA || 0),
          OT: Number(res.data.mentorFees.OT || 0),
          PT: Number(res.data.mentorFees.PT || 0),
          ST: Number(res.data.mentorFees.ST || 0),
        });
      }
      if (res.data?.meta) setMeta(res.data.meta);

      setTeacherName(selectedTeacher?.name || '');
      setTeacherAddress(res.data?.meta?.teacher_address || '');
    } catch (e) {
      console.error(e);
      setLoadError('Failed to fetch timeline report data.');
      setItems([]);
      setAdministrativePayroll(null);
      setSegments([]);
      setDailyTotals([]);
      setTotals({
        obj: { service_cost: 0, travel_cost: 0, bonus: 0, mentor_fee: 0, total: 0, paid_break_minutes: 0, paid_break_cost: 0, drive_minutes: 0, drive_hours: 0, distance_km: 0, distance_mi: 0 },
        subj: { service_cost: 0, travel_cost: 0, extra: 0, bonus: 0, mentor_fee: 0, total: 0, paid_break_minutes: 0, paid_break_cost: 0, drive_minutes: 0, drive_hours: 0, distance_km: 0, distance_mi: 0 },
      });
      setHoursBreakdown({
        obj: { visit_hours: 0, work_hours: 0, drive_hours: 0 },
        subj: { visit_hours: 0, work_hours: 0, drive_hours: 0 },
      });
      setRates(null);
      setMeta(null);
      setTeacherName('');
      setTeacherAddress('');
    } finally {
      setLoading(false);
    }
  };

  // --- Print CSS once ---
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .report-service-print-summary { display: none; }
      @media print {
        body { overflow: visible !important; }
        [data-report-details-modal], #teachers-report-print [data-report-details-trigger] { display: none !important; }
        #teachers-report-print .report-service-print-summary { display: block !important; }
        html, body, #teachers-report-print {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        body * { visibility: hidden !important; }
        #teachers-report-print, #teachers-report-print * { visibility: visible !important; }
        #teachers-report-print { position: absolute; left: 0; top: 0; width: 100vw; background: white; }
        #teachers-report-print thead { display: table-header-group; }
        #teachers-report-print tfoot { display: table-footer-group; }
        #teachers-report-print tr, #teachers-report-print img {
          break-inside: avoid; page-break-inside: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // --- Group items by date ---
  const groupedItems = useMemo(() => groupBy(items, (it) => getItemDateKey(it)), [items]);
  const dayKeys = useMemo(() => Object.keys(groupedItems).sort(), [groupedItems]);

  const visitsCountExcludingNoShow = useMemo(
    () => (items || []).filter((it) => !isWorkItem(it) && !it?.isNoShow).length,
    [items]
  );
  const workCount = useMemo(() => (items || []).filter((it) => isWorkItem(it)).length, [items]);

  const mentorFeesTotal = useMemo(() => (items || []).reduce((s, it) => s + (Number(it?.mentor_fee) || 0), 0), [items]);
  const bonusTotal = useMemo(() => Number(totals?.obj?.bonus || 0), [totals]);

  const legendRulesRaw = durationRules || defaultLegendDurationRules;

  const legendRules = useMemo(() => {
    return {
      EI: normalizeRuleForLegend(legendRulesRaw.EI, defaultLegendDurationRules.EI),
      ST: normalizeRuleForLegend(legendRulesRaw.ST, defaultLegendDurationRules.ST),
      PI: normalizeRuleForLegend(legendRulesRaw.PI, defaultLegendDurationRules.PI),
      ABA: normalizeRuleForLegend(legendRulesRaw.ABA, defaultLegendDurationRules.ABA),
      OT: normalizeRuleForLegend(legendRulesRaw.OT, defaultLegendDurationRules.OT),
      PT: normalizeRuleForLegend(legendRulesRaw.PT, defaultLegendDurationRules.PT),
      EVENT: normalizeRuleForLegend(legendRulesRaw.EVENT, defaultLegendDurationRules.EVENT),
      generic: {
        ...defaultLegendDurationRules.generic,
        ...(legendRulesRaw.generic || {}),
      },
    };
  }, [legendRulesRaw]);

  const noShowCap = Number(meta?.no_show_max_min ?? legendRules.generic?.NOSHOW_MAX_MIN ?? defaultLegendDurationRules.generic.NOSHOW_MAX_MIN);
  const paidBreakMinRule = Number(meta?.paid_break_min ?? legendRules.generic?.PAID_BREAK_MIN ?? defaultLegendDurationRules.generic.PAID_BREAK_MIN);
  const fourHoursMinRule = Number(meta?.four_hours_min ?? legendRules.generic?.FOUR_HOURS_MIN ?? defaultLegendDurationRules.generic.FOUR_HOURS_MIN);
  const payrollChainGapEnabled = meta?.payroll_chain_gap_enabled ?? legendRules.generic?.PAYROLL_CHAIN_GAP_ENABLED ?? true;
  const payrollChainGapMaxMin = Number(meta?.payroll_chain_gap_max_min ?? legendRules.generic?.PAYROLL_CHAIN_GAP_MAX_MIN ?? 90);
  const payrollEvvValidationEnabled = !!meta?.payroll_evv_validation_enabled;
  const payrollEvvExcludedCount = Number(meta?.payroll_evv_excluded_count || 0);
  const payrollEvvExceptionCount = Number(meta?.payroll_evv_exception_count || 0);
  const payrollEvvPendingCount = Number(meta?.payroll_evv_pending_count || 0);

  const mentorFeesLine = `Mentor fees per visit: EI $${fmtMoney(mentorFees.EI)} | ST $${fmtMoney(mentorFees.ST)} | PI $${fmtMoney(mentorFees.PI)} | ABA $${fmtMoney(mentorFees.ABA)} | OT $${fmtMoney(mentorFees.OT)} | PT $${fmtMoney(mentorFees.PT)}`;

  const showRatesLine = useMemo(() => {
    const r = rates || null;
    if (!r) return '';
    return `Rates: Visit $${fmtMoney(r.hourly_rate)}/h · Office $${fmtMoney(r.office_hourly_rate)}/h · Drive $${fmtMoney(r.drive_hourly_rate)}/h · Distance $${fmtMoney(r.travel_cost_per_mile)}/mi`;
  }, [rates]);

  const handlePrint = () => {
    if (!filters.teacher_id) {
      alert('Please choose a teacher.');
      return;
    }
    if (!items.length) {
      alert('No data to print for the selected period/teacher.');
      return;
    }
    window.print();
  };

  const handleExportExcel = () => {
    if (!items.length && !administrativePayroll?.summary?.approved_count) { alert('Generate a report before exporting.'); return; }
    try {
      const rows = [
        ['Teacher', teacherName, 'Period', filters.date_from, filters.date_to],
        ['System travel prioritizes GPS, then Google/ORS estimates subject to payroll policies. Teacher travel fields may use labeled system fallbacks; raw manual entries remain separate.'],
        ['Historical reports use current rates/rules and the selected GPS evidence or route estimates. Totals use full calculation precision.'],
        ['Date', 'Type', 'Item', 'Planned time', 'Payroll service min', 'Payroll service $', 'Payroll miles', 'Payroll drive min', 'Payroll travel $', 'Bonus $', 'Mentor $', 'Payroll item total $', 'Paid break min', 'Service basis', 'Service rule', 'Check-in', 'Check-out', 'Recorded service min', 'Route source', 'Travel policy', 'First commute unpaid', 'GPS trip ID', 'Recorded GPS miles', 'Recorded GPS min', 'GPS start', 'GPS stop', 'Tracking mode', 'AutoPilot session', 'Teacher service min', 'Teacher drive min', 'Teacher miles', 'Teacher extra $', 'Teacher service start', 'Teacher service end', 'Teacher drive start', 'Teacher drive end', 'Teacher break min', 'EVV status', 'Teacher service $', 'Service difference $ (Teacher-System)', 'Teacher travel $', 'Travel difference $ (Teacher-System)', 'Teacher item total $', 'Total difference $ (Teacher-System)', 'Complete teacher monetary estimate', 'Teacher log source', 'Teacher resolved miles', 'Teacher resolved drive min', 'Teacher miles source', 'Teacher drive source', 'Teacher travel fallback source', 'GPS route check', 'GPS tolerance %', 'Route estimate miles', 'Route estimate minutes', 'Comparison estimate provider', 'GPS distance deviation %', 'GPS time deviation %', 'Exceeded components', 'Teacher service minutes used', 'Teacher visit bonus $', 'System service status', 'Teacher service status', 'Service minimum min', 'Service maximum min', 'Accepted lower min', 'Accepted upper min', 'System service minutes before paid break'],
      ];
      const value = (v) => v == null ? '' : v;
      for (const it of items) {
        const o = it.obj || {}, r = it.recorded || {}, m = it.teacher_entry || {}, g = r.travel;
        rows.push([it.date, getItemKindNormalized(it), it.label, it.timeSlot,
          o.service_min, o.service_cost, o.distance_mi, o.route_minutes, o.travel_cost,
          it.bonus || 0, it.mentor_fee || 0, computeRowTotals(it).objRowTotal, it.paid_break_min,
          serviceBasisLabel(o.service_source), o.rule_label || '', value(r.checkin_time), value(r.checkout_time), value(r.service_minutes),
          systemTravelLabel(it), o.travel_policy_label || '', o.first_commute_unpaid ? 'Yes' : 'No',
          value(g?.trip_id), value(g?.miles), value(g?.minutes), value(g?.started_at), value(g?.stopped_at),
          tripModeLabel(g), value(g?.automation?.session_id), value(m.service_minutes), value(m.drive_minutes), value(m.miles), value(m.extra),
          value(m.service_start), value(m.service_end), value(m.drive_start), value(m.drive_end), value(m.break_minutes), it.evv_status || '', value(it.teacher_estimate?.service_cost), value(it.teacher_estimate?.difference_service), value(it.teacher_estimate?.travel_cost), value(it.teacher_estimate?.difference_travel), value(it.teacher_estimate?.total), value(it.teacher_estimate?.difference_total), it.teacher_estimate?.complete ? 'Yes' : 'No', m.source || '', value(it.teacher_trip?.miles), value(it.teacher_trip?.drive_minutes), it.teacher_trip?.miles_source || '', it.teacher_trip?.minutes_source || '', teacherTravelLabel(it), it.obj?.gps_route_check?.status || '', value(it.obj?.gps_route_check?.threshold_percent), value(it.obj?.gps_route_check?.estimate_miles), value(it.obj?.gps_route_check?.estimate_minutes), it.obj?.gps_route_check?.estimate_provider || '', value(it.obj?.gps_route_check?.distance_excess_percent), value(it.obj?.gps_route_check?.time_excess_percent), (it.obj?.gps_route_check?.exceeded || []).join(', '), value(it.teacher_estimate?.service_minutes), value(it.teacher_estimate?.bonus), it.service_assessment?.system?.status || '', it.service_assessment?.teacher?.status || '', value(it.service_assessment?.system?.minimum), value(it.service_assessment?.system?.maximum), value(it.service_assessment?.system?.lower_limit_minutes), value(it.service_assessment?.system?.upper_limit_minutes), value(it.obj?.service_min_base)]);
      }
      rows.push([]);
      rows.push(['Date', 'Payroll service $', 'Payroll travel $', 'Bonus $', 'Mentor $', 'Payroll total $', 'Paid break min', 'Teacher service $', 'Teacher travel $', 'Teacher total $', 'Difference $ (Teacher-System)', 'Complete teacher items', 'Total items']);
      for (const d of dailyTotals) rows.push([d.date, d.obj?.service_cost, d.obj?.travel_cost, d.obj?.bonus, d.obj?.mentor_fee, d.obj?.total, d.obj?.paid_break_minutes, value(d.teacher_estimate?.service_cost), value(d.teacher_estimate?.travel_cost), value(d.teacher_estimate?.total), value(d.teacher_estimate?.difference_total), d.teacher_estimate?.complete_count || 0, d.teacher_estimate?.item_count || 0]);
      rows.push(['TOTAL', totals?.obj?.service_cost, totals?.obj?.travel_cost, bonusTotal, mentorFeesTotal, totals?.obj?.total, totals?.obj?.paid_break_minutes, value(teacherPeriodComparison.service), value(teacherPeriodComparison.travel), value(teacherPeriodComparison.total), value(teacherPeriodComparison.difference), teacherPeriodComparison.count, items.length]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), 'WorkTimeline');
      if (administrativePayroll?.available) {
        const approvedRows = [
          ['Effective payroll — alternative calculation; do not add to System or Teacher totals'],
          ['Date','Service $','Travel $','Bonus $','Mentor $','Total $','Paid break min','Visit hours','Work hours','Active approvals'],
          ...administrativePayroll.days.map(d => [d.date,d.service_cost,d.travel_cost,d.bonus,d.mentor_fee,d.total,d.paid_break_minutes,d.visit_hours,d.work_hours,d.approved_count]),
          ['TOTAL',...['service_cost','travel_cost','bonus','mentor_fee','total','paid_break_minutes','visit_hours','work_hours','approved_count'].map(k => administrativePayroll.summary[k])],
          [], ['Visit ID','Date','Label','Status','EVV','Review state'],
          ...administrativePayroll.candidates.map(v => [v.visit_id,v.date,v.label,v.status,v.evv_status,v.review_status]),
          [], ['Visit ID','Review ID','Revision','Source','Payable min','Paid break min','Service $','Travel $','Bonus $','Mentor $','Total $'],
          ...items.filter(it => it.approved_payroll).map(it => [it.id,it.approved_payroll.review_id,it.approved_payroll.revision,...['source','service_minutes','paid_break_minutes','service_cost','travel_cost','bonus','mentor_fee','total'].map(k => it.approved_payroll[k])]),
          ...administrativePayroll.supplemental.map(v => [v.visit_id,v.review.id,v.review.revision,...['source','service_minutes','paid_break_minutes','service_cost','travel_cost','bonus','mentor_fee','total'].map(k => v.approved_payroll[k])])
        ];
        approvedRows.push([], ['Visit ID','Review ID','Revision','Confirmed start','Confirmed end','Timezone','Payroll decision','Payable minutes','Reason','Payroll exception','Reviewed by user ID']);
        const reviewSources = [...items.filter(it => it.administrative_review).map(it => ({visitId:it.id,review:it.administrative_review})), ...administrativePayroll.supplemental.map(v => ({visitId:v.visit_id,review:v.review}))];
        for (const {visitId,review} of reviewSources) approvedRows.push([visitId,review.id,review.revision,review.approved_start_time,review.approved_end_time,review.service_timezone,review.payroll_decision,review.approved_payable_minutes,review.reason_text,review.payroll_exception_reason || '',review.reviewed_by_user_id]);
        XLSX.utils.book_append_sheet(workbook,XLSX.utils.aoa_to_sheet(approvedRows),'AdministrativePayroll');
      }
      XLSX.writeFile(workbook, 'TeachersWorkTimelineReport.xlsx');
    } catch (error) { console.error('Excel export failed:', error); alert('Export failed'); }
  };

  const teacherPeriodComparison = useMemo(() => {
    const sumComplete = (field) => dailyTotals.length > 0 && dailyTotals.every((day) => day.teacher_estimate?.[field] != null) ? dailyTotals.reduce((sum, day) => sum + Number(day.teacher_estimate[field]), 0) : null;
    const service = sumComplete('service_cost');
    const travel = sumComplete('travel_cost');
    const total = sumComplete('total');
    return { service, travel, difference_service: service == null ? null : service - Number(totals?.obj?.service_cost || 0), difference_travel: travel == null ? null : travel - Number(totals?.obj?.travel_cost || 0), total, difference: total == null ? null : total - Number(totals?.obj?.total || 0),
      count: dailyTotals.reduce((sum, day) => sum + Number(day.teacher_estimate?.complete_count || 0), 0) };
  }, [dailyTotals, totals]);

  const teacherFooterSummary = useMemo(() => summarizeTeacherEntries(items), [items]);

  // ------- per-row totals helper -------
  const computeRowTotals = (it) => {
    const obj = it?.obj || {};
    const isVisit = !isWorkItem(it);

    const objRowTotal =
      Number(obj.service_cost || 0) +
      Number(obj.travel_cost || 0) +
      (isVisit ? (Number(it?.bonus || 0) + Number(it?.mentor_fee || 0)) : 0);

    return { objRowTotal };
  };

  return (
    <div className="p-4 text-sm">
      <LoadingToast show={loading} text="Generating report…" />

      <h2 className="text-lg font-bold mb-4 no-print">Teacher Work Timeline Payroll Report</h2>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-4 no-print">
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          className="border p-1 rounded"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          className="border p-1 rounded"
        />
        <select
          value={filters.teacher_id}
          onChange={(e) => setFilters((f) => ({ ...f, teacher_id: e.target.value }))}
          className="border p-1 rounded"
        >
          <option value="">Choose teacher</option>
          {teachers.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-2 mb-2 no-print">
        <button
          onClick={fetchReport}
          disabled={loading}
          className={['px-4 py-2 rounded text-white', loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600'].join(' ')}
        >
          {loading ? 'Working…' : 'Apply Filters'}
        </button>
        <button
          onClick={handleExportExcel}
          disabled={loading}
          className={['px-4 py-2 rounded text-white', loading ? 'bg-green-300 cursor-not-allowed' : 'bg-green-500'].join(' ')}
        >
          Export Excel
        </button>
        <button
          onClick={handlePrint}
          disabled={loading}
          className={['px-4 py-2 rounded text-white', loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-600'].join(' ')}
        >
          Print
        </button>
      </div>

      {/* Report */}
      <div id="teachers-report-print" ref={reportRef} className="bg-white max-w-6xl mx-auto p-2">
        <div className="print-header mb-4 flex flex-col gap-1 border rounded p-2 bg-gray-50">
          <div className="flex items-center gap-3">
            <img src={brandLogoUrl || '/VisitOMS_logo.png'} alt={brandName || 'Brand logo'} className="w-14 h-14 object-contain" />
            <div>
              <div className="text-base font-semibold">Teacher Timeline Payroll Report</div>
              {brandName && <div className="text-xs text-gray-600">{brandName}</div>}
              <div className="text-[10px] text-gray-400">Generated with VisitOMS EarlyCare software - www.visitOMS.com</div>
            </div>
          </div>

          <div>
            <strong>Teacher:</strong> {safe(teacherName, '—')}
          </div>
          {teacherAddress ? (
            <div>
              <strong>Address:</strong> {teacherAddress}
            </div>
          ) : null}
          <div>
            <strong>Period:</strong> {filters.date_from || '—'} – {filters.date_to || '—'}
          </div>

          <div className="flex flex-wrap gap-4">
            <div>
              <strong>Visits (excluding No-show):</strong> {visitsCountExcludingNoShow}
            </div>
            <div>
              <strong>Work blocks:</strong> {workCount}
            </div>
            <div>
              <strong>Date of print:</strong> {(serverTimeStr || '').slice(0, 10) || new Date().toISOString().slice(0, 10)}
            </div>
          </div>

          {showRatesLine ? <div className="text-[11px] text-slate-600">{showRatesLine}</div> : null}

          {payrollEvvValidationEnabled ? (
            <div className="mt-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
              <strong>EVV payroll validation is ON.</strong> EVV-required visits need complete EVV status to appear in the original System / Teacher comparisons. Active administrative service payroll approvals are shown separately.
              {payrollEvvExcludedCount > 0 ? (
                <span> Excluded: {payrollEvvExcludedCount} ({payrollEvvExceptionCount} exception, {payrollEvvPendingCount} pending).</span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mb-2 p-2 border rounded bg-gray-50 flex flex-wrap gap-4">
          <span><strong>Payroll service:</strong> {toFixed(hoursBreakdown?.obj?.visit_hours)} visit hours · {toFixed(hoursBreakdown?.obj?.work_hours)} work hours</span>
          <span><strong>Paid travel:</strong> {toFixed(totals?.obj?.distance_mi)} mi · {toFixed(totals?.obj?.drive_minutes)} min · ${fmtMoney(totals?.obj?.travel_cost)}</span>
          <span><strong>Payroll total:</strong> ${fmtMoney(totals?.obj?.total)}</span>
        </div>

        {items.length > 0 && meta?.evidence_schema_version !== 8 && <div className="mb-2 border border-amber-300 bg-amber-50 p-2 text-amber-900">Evidence details are unavailable from this backend version. Update the backend and regenerate the report before interpreting evidence or monetary comparisons.</div>}

        {/* LEGEND */}
        <div className="mb-4 p-2 border rounded bg-white text-[11px] text-gray-700">
          <div className="font-semibold mb-1">Legend – Timeline payroll (Visits + Work + Chaining travel)</div>

          <p className="mb-1">
            <span className="font-semibold">Miles/Route</span> columns show the selected system travel after payroll rules: qualifying GPS Trip (including AutoPilot) → Google Routes → ORS → saved estimate. GPS exceeding either estimated distance or duration by more than {meta?.gps_route_max_excess_percent ?? 50}% uses the whole estimated route. Long-gap policy takes precedence and skips this tolerance check; unpaid first commute/no-travel policies override the payable amount. System amounts follow payroll rules. Teacher estimates price entered service and travel at the same rates, filling missing travel fields from the selected system source with explicit fallback labels; Δ means Teacher minus System. They are alternative comparisons, not additional payable amounts. Complete teacher totals require service time and resolved travel inputs for every included item. Missing service time remains unavailable; it is never derived from GPS travel. A dash means no recorded value. GPS evidence does not prove that a check-in/out was automatic.
          </p>
          <p className="mb-2">GPS evidence is shown only for qualifying trips (at least 50 points, 5 minutes, up to 6 hours and 200 miles); missing evidence does not prove no travel occurred. When multiple trips qualify for an item, the longest is shown. Recorded times use the organization reporting timezone.</p>

          <p className="mb-2">Visit service: below {100 - (meta?.service_min_shortfall_percent ?? 50)}% of the discipline minimum → $0 visit service and no visit bonus, pending review. From that lower limit up to {100 + (meta?.service_max_excess_percent ?? 0)}% of the discipline maximum → actual minutes; above that allowance → capped at the discipline maximum. {Number(meta?.service_max_excess_percent ?? 0) === 0 ? 'Upper tolerance is 0%: the maximum is a hard cap.' : 'Upper allowance follows the saved organization setting.'} No minimum uplift. Raw times remain visible. Work and no-show use their separate rules; mentor fees, travel and paid breaks are separate.</p>
          <div className="mb-2 text-[11px] text-indigo-700 font-semibold">{mentorFeesLine}</div>

          <ul className="list-disc ml-4 space-y-0.5">
            <li>EI: default {legendRules.EI.default} min; range {legendRules.EI.min}–{legendRules.EI.max} min.</li>
            <li>ST: default {legendRules.ST.default} min; range {legendRules.ST.min}–{legendRules.ST.max} min.</li>
            <li>PI: default {legendRules.PI.default} min; range {legendRules.PI.min}–{legendRules.PI.max} min.</li>
            <li>ABA (standard): default {legendRules.ABA.default} min; configured range {legendRules.ABA.min}–{legendRules.ABA.max} min.</li>
            <li>
              OT: default {legendRules.OT.default} min; configured range {legendRules.OT.min}–{legendRules.OT.max} min. PT: default {legendRules.PT.default} min; configured range {legendRules.PT.min}–{legendRules.PT.max} min.
            </li>
            <li>Telehealth visit / Remote work: no travel distance/time (coords are null).</li>
            <li>No-show: payroll service uses NOSHOW_MAX_MIN from settings (current cap {noShowCap} min{Number(noShowCap) <= 0 ? ' → disabled' : ''}).</li>
            <li>
              Paid breaks: every {fourHoursMinRule} minutes ({minutesToHours(fourHoursMinRule)} h) of combined payroll work
              (visit service base + work service base + paid drive minutes) triggers {paidBreakMinRule} paid minutes.
            </li>
            {payrollChainGapEnabled ? (
              <li>
                If the gap between two scheduled items exceeds {payrollChainGapMaxMin} minutes, payroll pays the route estimate to the next item and ignores actual GPS/manual travel through the long gap.
              </li>
            ) : (
              <li>
                Long-gap protection is disabled for this organization; chained travel follows the regular route policy between scheduled items.
              </li>
            )}
            <li>Any break in between the visits can be used as lunch break and it is considered as unpaid break.</li>
            <li>
              EVV payroll validation: {payrollEvvValidationEnabled ? 'ON - complete EVV required for original comparisons; separate administrative service approvals may apply.' : 'OFF - existing completed/no-show payroll rules apply.'}
            </li>
          </ul>

          {meta?.clinic_default_location_id ? (
            <div className="mt-2 text-[10px] text-slate-500">Clinic default location id: {meta.clinic_default_location_id}</div>
          ) : null}
        </div>

        {loadError ? (
          <div className="p-4 text-red-600">{loadError}</div>
        ) : items.length === 0 ? (
          <div className="p-4">No items in the original System / Teacher comparison. Administrative approvals, when present, are shown separately below.</div>
        ) : (
          dayKeys.map((date) => {
            const dayItems = groupedItems[date] || [];
            const subtotal = dailyTotals.find((dt) => dt.date === date) || null;

            const teacherDaySummary = summarizeTeacherEntries(dayItems);
            const dayObjHoursSum = dayItems.reduce((s, it) => s + Number(it?.obj?.service_min || 0) / 60, 0);

            const showPaidBreakPanel =
              !!subtotal && Number(subtotal?.obj?.paid_break_minutes || 0) > 0;


            return (
              <div key={date} className="mb-8 print:mb-2">
                <h3 className="font-semibold text-blue-700 mb-1">{date}</h3>

                {showPaidBreakPanel && <div className="mb-2 text-xs bg-blue-50 text-blue-800 border rounded p-2">Paid breaks: {subtotal?.paid_breaks_count || 0} × {paidBreakMinRule} min · Included in payroll service.</div>}

                <table className="w-full border border-gray-200 text-xs mb-2 print:text-[11px]">
                  <colgroup>
                    <col style={{ width: '260px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '95px' }} />
                    <col style={{ width: '85px' }} />
                    <col style={{ width: '95px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '120px' }} />
                  </colgroup>

                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-1 text-left">Item</th>
                      <th className="border p-1 text-left">Planned time</th>
                      <th className="border p-1 text-right">Hours</th>
                      <th className="border p-1 text-right">Miles</th>
                      <th className="border p-1 text-right">Route time (min)</th>
                      <th className="border p-1 text-right">Service ($)</th>
                      <th className="border p-1 text-right">Travel ($)</th>
                      <th className="border p-1 text-right">Extra ($)</th>
                      <th className="border p-1 text-right">Bonus / Mentor</th>
                      <th className="border p-1 text-right">Item total ($)</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dayItems.map((it) => {
                      const isWork = isWorkItem(it);
                      const isVisit = !isWork;

                      const obj = it?.obj || {};

                      const objHours = Number(obj.service_min || 0) / 60;

                      const paidBreakMin = Number(it?.paid_break_min || 0);

                      let rowClass = 'align-top hover:bg-blue-50 print:bg-white';
                      if (isWork) rowClass += ' bg-sky-50/40';
                      if (isVisit && it?.isNoShow) rowClass += ' bg-purple-50';

                      const trainingFlag = String(it?.training_flag || 'none').toLowerCase();
                      const showMentor = isVisit && trainingFlag !== 'none';

                      const travelDistText = milesOnlyDistanceFormula(obj.travel_formula_distance);

                      const { objRowTotal } = computeRowTotals(it);

                      const objSourceHint = String(obj.travel_formula_distance || '');
                      const isFallback = objSourceHint.toLowerCase().includes('fallback');

                      return (
                        <tr key={`${getItemKindNormalized(it)}_${it.id}`} className={rowClass}>
                          <td className="border p-1">
                            <div className="flex items-start gap-2">
                              <div className="min-w-0 w-full">
                                <div className="space-y-1">
                                  <div className={['w-full break-words', isWork ? 'font-semibold text-sky-800' : ''].join(' ')}>
                                    {safe(it?.label)}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2">
                                  {paidBreakMin > 0 && (
                                    <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-[9px] font-semibold">
                                      Paid break +{paidBreakMin}m
                                    </span>
                                  )}
                                  {isFallback && (
                                    <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[9px] font-semibold">
                                      Travel estimate fallback
                                    </span>
                                  )}
                                  <TravelPolicyBadge policy={obj.travel_policy} gapMinutes={obj.gap_minutes} />
                                  {isVisit && administrativePayroll?.available && <button className="print:hidden shrink-0 whitespace-nowrap rounded border border-indigo-200 px-2 py-1 text-[10px] text-indigo-700" onClick={() => setReviewVisitId(it.id)}>Review service & payroll</button>}
                                  </div>
                                </div>

								<div className="flex flex-wrap gap-1 mt-0.5 text-[9px]">
								  {isVisit ? (
								    <>
								      {it?.discipline_service_type ? <DisciplineBadge serviceType={it.discipline_service_type} /> : null}

								      {it?.isNoShow ? (
								        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
								          No-show (cap {noShowCap}m)
								        </span>
								      ) : null}

								      {showMentor ? <MentorBadge stage={trainingFlag} amount={Number(it?.mentor_fee || 0)} /> : null}

								      {/* ✅ NEW: show how subj HOURS were sourced */}
								      <span className="text-slate-600">Service: {it.obj?.service_source === 'checkin_checkout' ? 'Check-in/out + rules' : 'Fallback + rules'}</span>

								      {/* ✅ Optional: show travel source too (GPS/manual/fallback) */}
								      <span className="text-slate-600">{systemTravelLabel(it)}</span>
								      <EvvBadge required={it?.evv_required} status={it?.evv_status} />
								    </>
								  ) : (
								    <>
								      <WorkBadge workType={it?.work_type} />
								      {/* For work blocks, service_source may not exist (unless you add later) */}
								      <span className="text-slate-600">{systemTravelLabel(it)}</span>
								    </>
								  )}
								</div>
                                

                                {isVisit && it?.isTelehealth ? (
                                  <div className="mt-0.5 text-[9px]">
                                    <TelehealthBadge />
                                  </div>
                                ) : null}

                                <div className="mt-1 text-[10px] text-gray-500 italic">
                                  Payroll travel: {safe(travelDistText || obj.travel_formula_distance, '-')} · {safe(obj.travel_formula_time, '-')}{' '}
                                  {obj.travel_formula_cost ? `· ${safe(obj.travel_formula_cost, '-')}` : ''}
                                </div>
                                {obj.first_commute_unpaid && <div className="text-[10px] text-amber-700">First commute — unpaid; GPS evidence is shown separately.</div>}
                                {it.evv_status === 'exception' && <div className="text-[10px] text-red-700">EVV exception — review recorded check-in/out duration.</div>}
                                <div className="text-[10px] text-slate-600">{tripModeLabel(it.recorded?.travel)}</div>
                                <GpsRouteCheck item={it} compact />
                                <button type="button" data-report-details-trigger aria-haspopup="dialog" onClick={() => setCalculationSelection({ item: it })} className="mt-2 text-[11px] font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900">Calculation details</button>
                              </div>
                            </div>
                          </td>

                          <td className="border p-1">{safe(it?.timeSlot, '')}</td>

                          <td className="border p-1 text-right">
                            <div className="font-medium">{toFixed(objHours)} h</div>
                            <div className="text-[10px] text-slate-600">Payroll: {toFixed(obj.service_min)} min</div>
                            <div className="text-[10px]">Recorded: {evidenceNumber(it.recorded?.service_minutes, ' min')}</div>
                            <div className="text-[10px] text-gray-500">Teacher entered: {evidenceNumber(it.teacher_entry?.service_minutes, ' min')} <span className="text-indigo-700">(for calculation: {evidenceNumber(it.teacher_estimate?.service_minutes, ' min')})</span></div><div className="text-[10px]">Teacher calculation: {evidenceNumber(it.teacher_estimate?.service_minutes, ' min')}</div><ServiceAssessmentLink item={it} model="system" onOpen={setCalculationSelection} /><ServiceAssessmentLink item={it} model="teacher" onOpen={setCalculationSelection} />
                          </td>
                          <td className="border p-1 text-right">
                            <div className="font-medium">System: {toFixed(obj.distance_mi)} mi</div><div className="text-[10px] text-slate-600">{systemTravelLabel(it)}</div>
                            <div className="text-[10px]">GPS: {evidenceNumber(it.recorded?.travel?.miles, ' mi')}</div>
                            <TeacherTripQuantity item={it} field="miles" suffix=" mi" />
                          </td>
                          <td className="border p-1 text-right">
                            <div className="font-medium">System: {toFixed(obj.route_minutes)}</div><div className="text-[10px] text-slate-600">{systemTravelLabel(it)}</div>
                            <div className="text-[10px]">GPS: {evidenceNumber(it.recorded?.travel?.minutes, ' min')}</div>
                            <TeacherTripQuantity item={it} field="drive_minutes" suffix=" min" />
                          </td>
                          <td className="border p-1 text-right"><MoneyComparison system={obj.service_cost} estimate={it.teacher_estimate?.service_cost} difference={it.teacher_estimate?.difference_service} /><TeacherServiceCalculation item={it} />{it.administrative_review && <div className="mt-1 text-[10px] text-emerald-800">Admin revision {it.administrative_review.revision}: {it.administrative_review.fresh ? it.administrative_review.payroll_decision.replaceAll('_', ' ') : 'stale — not applied'}</div>}{it.approved_payroll?.source === 'admin_confirmed' && <div className="text-[10px] text-emerald-800">Effective: {toFixed(it.approved_payroll.service_minutes)} min · {comparisonMoney(it.approved_payroll.service_cost)} (including {toFixed(it.approved_payroll.paid_break_minutes)} min paid break)</div>}</td>
                          <td className="border p-1 text-right"><MoneyComparison system={obj.travel_cost} estimate={it.teacher_estimate?.travel_cost} difference={it.teacher_estimate?.difference_travel} /><div className="text-[10px] text-slate-600">System: {systemTravelLabel(it)}</div><div className="text-[10px] text-slate-500">{teacherTravelLabel(it)}</div></td>
                          <td className="border p-1 text-right text-gray-500">{evidenceNumber(it.teacher_entry?.extra, ' USD')}<div className="text-[10px]">Teacher entry</div></td>
                          <td className="border p-1 text-right">
                            <div className="font-medium">System: ${fmtMoney(isVisit ? Number(it.bonus || 0) : 0)}</div><div className="text-gray-500">Teacher: {comparisonMoney(it.teacher_estimate?.bonus)}</div>
                            <div className="text-gray-500">Mentor ${fmtMoney(isVisit ? Number(it.mentor_fee || 0) : 0)}</div>
                          </td>
                          <td className="border p-1 text-right"><MoneyComparison system={objRowTotal} estimate={it.teacher_estimate?.total} difference={it.teacher_estimate?.difference_total} />{!it.teacher_estimate?.complete && <div className="text-[10px] text-slate-500">Service/travel data incomplete</div>}</td>
                        </tr>
                      );
                    })}

                    {subtotal && <>
                      <tr className="font-semibold bg-blue-50">
                        <td className="border p-2" colSpan={2}>System calculation — day total<div className="text-[10px] font-normal text-slate-600">{travelSourceSummary(dayItems)} · service review: {dayItems.filter((it) => it.service_assessment?.system?.requires_review).length}</div></td>
                        <td className="border p-2 text-right">{toFixed(dayObjHoursSum)}</td><td className="border p-2 text-right">{evidenceNumber(subtotal.obj?.distance_mi)}</td>
                        <td className="border p-2 text-right">{evidenceNumber(subtotal.obj?.drive_minutes)}</td><td className="border p-2 text-right">{comparisonMoney(subtotal.obj?.service_cost)}</td>
                        <td className="border p-2 text-right">{comparisonMoney(subtotal.obj?.travel_cost)}</td><td className="border p-2 text-right">—</td>
                        <td className="border p-2 text-right">{comparisonMoney(subtotal.obj?.bonus)}<div className="text-[10px]">Mentor: {comparisonMoney(subtotal.obj?.mentor_fee)}</div></td><td className="border p-2 text-right">{comparisonMoney(subtotal.obj?.total)}</td>
                      </tr>
                      <tr className="font-semibold bg-amber-50">
                        <td className="border p-2" colSpan={2}>Teacher calculation — day total<TeacherCoverage summary={teacherDaySummary} /></td>
                        <td className="border p-2 text-right"><TeacherHours entered={teacherDaySummary.rawHours} used={teacherDaySummary.hours} /></td><td className="border p-2 text-right">{evidenceNumber(teacherDaySummary.miles)}</td>
                        <td className="border p-2 text-right">{evidenceNumber(teacherDaySummary.driveMinutes)}</td><td className="border p-2 text-right">{comparisonMoney(teacherDaySummary.service)}</td>
                        <td className="border p-2 text-right">{comparisonMoney(teacherDaySummary.travel)}</td><td className="border p-2 text-right">{comparisonMoney(teacherDaySummary.extra)}</td>
                        <td className="border p-2 text-right">{comparisonMoney(teacherDaySummary.bonus)}<div className="text-[10px]">Mentor: {comparisonMoney(teacherDaySummary.mentor)}</div></td><td className="border p-2 text-right">{comparisonMoney(teacherDaySummary.total)}</td>
                      </tr>
                      <ApprovedPayrollRow daily value={subtotal.approved_payroll} />
                      <tr className="text-indigo-700"><td colSpan={9} className="border p-2">Difference: Teacher − System{!teacherDaySummary.complete ? ' · unavailable until service/travel inputs are complete' : ''}</td><td className="border p-2 text-right">{comparisonMoney(teacherDaySummary.complete ? teacherDaySummary.total - Number(subtotal.obj?.total || 0) : null, true)}</td></tr>
                    </>}
                  </tbody>
                </table>
              </div>
            );
          })
        )}

        {administrativePayroll && <section className="my-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 print:break-inside-avoid">
          <h3 className="font-semibold text-emerald-950">Administrative service review</h3>
          <p className="mt-1 text-xs text-slate-600">Confirm actual service and approve payroll separately. Available for draft and completed visits, regardless of EVV status. System and Teacher comparisons retain their original evidence.</p>
          {!administrativePayroll.available ? <p role="alert" className="mt-2 text-sm text-red-800">{administrativePayroll.notice}</p> : <>
            <div className="mt-3 flex flex-wrap gap-2 print:hidden"><label className="flex-1 min-w-[200px]"><span className="sr-only">Visit to review</span><select value={candidateId} onChange={e => setCandidateId(e.target.value)} className="w-full rounded-lg border bg-white p-2 text-sm"><option value="">Select a visit to review…</option>{administrativePayroll.candidates.map(v => <option key={v.visit_id} value={v.visit_id}>{v.date} · {v.label} · {v.status} · EVV {v.evv_status} · {v.review_status.replaceAll('_', ' ')}</option>)}</select></label><button disabled={!candidateId} onClick={() => setReviewVisitId(Number(candidateId))} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-40">Review service & payroll</button></div>
            {administrativePayroll.stale_count > 0 && <p className="mt-2 text-sm text-amber-900">{administrativePayroll.stale_count} approval(s) require another review because evidence or policy changed. Those approvals are not applied.</p>}
            {administrativePayroll.supplemental.map(v => <div key={v.visit_id} className="mt-3 rounded-lg border bg-white p-3 text-sm"><strong>{v.date} · {v.label} · administrative approval #{v.review.id}</strong><p>{v.review.approved_start_time} → {v.review.approved_end_time} · payable {Number(v.approved_payroll.service_minutes).toFixed(2)} min · service {comparisonMoney(v.approved_payroll.service_cost)} · total {comparisonMoney(v.approved_payroll.total)}</p><p className="text-xs text-slate-600">Visit: {v.status} · EVV: {v.evv_status}. {v.notice}</p></div>)}
            <p className="mt-2 text-xs text-slate-600">{administrativePayroll.notice}</p>
          </>}
        </section>}

        {items.length > 0 && <div className="mb-4 text-sm">
          <h3 className="font-semibold">Final calculation — selected period</h3>
          <PeriodCalculations system={totals?.obj} hours={hoursBreakdown?.obj} teacher={teacherFooterSummary} items={items} approved={administrativePayroll?.available ? administrativePayroll.summary : null} />
          <p className="text-xs text-slate-600">System amounts follow payroll rules. Teacher amounts use manual service time and manual travel or clearly labeled system travel fallbacks at the listed rates. Partial teacher totals sum available service/travel amounts, declared extras and model-specific visit bonuses and unchanged mentor fees for included comparison items. Only missing travel quantities use system fallbacks; original manual entries remain unchanged. Differences are shown only for complete comparisons. These are alternative calculations, not amounts to add together.</p>
          <p className="mt-1 text-xs text-slate-600">System paid breaks: {evidenceNumber(totals?.obj?.paid_break_minutes, ' min')} ({comparisonMoney(totals?.obj?.paid_break_cost)}), included in system service. Teacher-declared breaks: {evidenceNumber(teacherFooterSummary.breakMinutes, ' min')}, shown as reported and not added automatically. Totals use full calculation precision. Historical reports use current rates/rules and the selected GPS evidence or route estimates.</p>
        </div>}

        {administrativePayroll?.available && administrativePayroll.days.filter(d => !dailyTotals.some(x => x.date === d.date)).map(d => <div key={d.date} className="my-3 overflow-x-auto"><h3 className="font-semibold">{d.date} — administratively approved service</h3><table className="w-full text-xs"><tbody><ApprovedPayrollRow value={d} /></tbody></table></div>)}
        {administrativePayroll?.available && items.length === 0 && administrativePayroll.summary.approved_count > 0 && <div className="my-3 overflow-x-auto"><h3 className="font-semibold">Effective payroll — selected period</h3><table className="w-full text-xs"><tbody><ApprovedPayrollRow value={administrativePayroll.summary} /></tbody></table></div>}
      </div>
      {calculationSelection && <CalculationDetailsDialog selection={calculationSelection} onClose={() => setCalculationSelection(null)} />}
      {reviewVisitId && <VisitServiceReviewDialog visitId={reviewVisitId} onClose={() => setReviewVisitId(null)} onSaved={fetchReport} />}
    </div>
  );
}



